/* All CRUD modals — scheme, event, template, user, search.
   Each follows the same pattern: simple form → POST/PUT → store.reload() → close. */
const { useState: useStateM, useEffect: useEffectM, useRef: useRefM } = React;

const api = () => window.QH_API;
const reload = () => window.QH_STORE.reload();

// ───────── Scheme ─────────
function SchemeCreateModal({ onClose }) {
  const [code, setCode] = useStateM('');
  const [name, setName] = useStateM('');
  const [desc, setDesc] = useStateM('');
  const [busy, setBusy] = useStateM(false);
  const [err, setErr] = useStateM('');

  const submit = async (e) => {
    e?.preventDefault();
    if (!code || !name) { setErr('Code and name are required.'); return; }
    setBusy(true); setErr('');
    try {
      await api().post('/schemes', { code, name, description: desc });
      await reload();
      onClose();
    } catch (e) { setErr(e.message || 'Failed.'); } finally { setBusy(false); }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header"><h3>New scheme</h3><button className="icon-btn close" onClick={onClose}><I.X/></button></div>
        <form className="modal-body" onSubmit={submit} style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12}}>
          <div className="field"><label>Code</label><input className="input font-mono" value={code} onChange={e => setCode(e.target.value)} placeholder="QH-XXXX-26" autoFocus/></div>
          <div className="field"><label>Name</label><input className="input" value={name} onChange={e => setName(e.target.value)} placeholder="Scheme name"/></div>
          <div className="field" style={{gridColumn: '1 / -1'}}><label>Description</label><textarea className="textarea" value={desc} onChange={e => setDesc(e.target.value)}/></div>
          {err && <div style={{gridColumn: '1 / -1', color: 'var(--neg)', fontSize: 12}}>{err}</div>}
          <div style={{gridColumn: '1 / -1', fontSize: 12, color: 'var(--muted)'}}>Maturity is set per-transaction — Commitment vs Normal is chosen when recording each deposit.</div>
        </form>
        <div className="modal-footer">
          <button className="btn ghost" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn primary" onClick={submit} disabled={busy}><I.Check/> {busy ? 'Saving…' : 'Create'}</button>
        </div>
      </div>
    </div>
  );
}

// ───────── Event ─────────
function EventCreateModal({ onClose, defaultSchemeId }) {
  const [schemeId, setSchemeId] = useStateM(defaultSchemeId || (QH_DATA.schemes[0]?.id || ''));
  const [name, setName] = useStateM('');
  const [date, setDate] = useStateM(new Date().toISOString().slice(0, 10));
  const [defaultBankId, setDefaultBankId] = useStateM('');
  const [busy, setBusy] = useStateM(false);
  const [err, setErr] = useStateM('');
  const activeBanks = (QH_DATA.banks || []).filter(b => b.active);

  const submit = async (e) => {
    e?.preventDefault();
    if (!schemeId || !name) { setErr('Scheme and name are required.'); return; }
    setBusy(true); setErr('');
    try {
      await api().post('/events', { schemeId, name, date, defaultBankId: defaultBankId || null });
      await reload();
      onClose();
    } catch (e) { setErr(e.message || 'Failed.'); } finally { setBusy(false); }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header"><h3>New event</h3><button className="icon-btn close" onClick={onClose}><I.X/></button></div>
        <form className="modal-body" onSubmit={submit} style={{display: 'flex', flexDirection: 'column', gap: 12}}>
          <div className="field"><label>Scheme</label><select className="select" value={schemeId} onChange={e => setSchemeId(e.target.value)} disabled={!!defaultSchemeId}>{QH_DATA.schemes.map(s => <option key={s.id} value={s.id}>{s.name} · {s.code}</option>)}</select></div>
          <div className="field"><label>Event name</label><input className="input" value={name} onChange={e => setName(e.target.value)} placeholder="Q1 Drive, Eid Round, …" autoFocus/></div>
          <div className="field"><label>Date</label><input className="input font-mono" type="date" value={date} onChange={e => setDate(e.target.value)}/></div>
          <div className="field">
            <label>Default bank <span className="text-xs muted" style={{textTransform:'none',letterSpacing:0}}>(optional — pre-selected when recording transactions for this event)</span></label>
            <select className="select" value={defaultBankId} onChange={e => setDefaultBankId(e.target.value)}>
              <option value="">— None —</option>
              {activeBanks.map(b => <option key={b.id} value={b.id}>{b.name}{b.code ? ` · ${b.code}` : ''}</option>)}
            </select>
            {activeBanks.length === 0 && <div className="text-xs muted" style={{marginTop:4}}>No active banks. Add one under Directory → Banks first.</div>}
          </div>
          {err && <div style={{color: 'var(--neg)', fontSize: 12}}>{err}</div>}
        </form>
        <div className="modal-footer">
          <button className="btn ghost" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn primary" onClick={submit} disabled={busy}><I.Check/> {busy ? 'Saving…' : 'Create'}</button>
        </div>
      </div>
    </div>
  );
}

// ───────── Template (create + edit) ─────────
function TemplateModal({ onClose, template }) {
  const isEdit = !!template;
  const [id, setId] = useStateM(template?.id || '');
  const [name, setName] = useStateM(template?.name || '');
  const [channel, setChannel] = useStateM(template?.channel || 'both');
  const [scope, setScope] = useStateM(template?.scope || 'all schemes');
  const [trigger, setTrigger] = useStateM(template?.trigger || '');
  const [schemeId, setSchemeId] = useStateM(template?.schemeId || '');
  const [body, setBody] = useStateM(template?.body || '');
  const [busy, setBusy] = useStateM(false);
  const [err, setErr] = useStateM('');

  const submit = async (e) => {
    e?.preventDefault();
    if ((!isEdit && !id) || !name || !body) { setErr('Id, name and body are required.'); return; }
    if (!trigger) { setErr('Trigger is required so the dispatcher knows when to use this template.'); return; }
    setBusy(true); setErr('');
    try {
      // trigger + schemeId are part of the dispatch lookup. Without
      // them, the message rendering path falls through to a generic
      // fallback because pickTemplate(trigger, schemeId) never matches.
      const payload = { name, channel, scope, body, trigger, schemeId: schemeId || null };
      if (isEdit) await api().put(`/templates/${template.id}`, payload);
      else await api().post('/templates', { id, ...payload });
      await reload();
      onClose();
    } catch (e) { setErr(e.message || 'Failed.'); } finally { setBusy(false); }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal lg" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header"><h3>{isEdit ? 'Edit template' : 'New template'}</h3><button className="icon-btn close" onClick={onClose}><I.X/></button></div>
        <form className="modal-body" onSubmit={submit} style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12}}>
          {!isEdit && <div className="field"><label>Template id</label><input className="input font-mono" value={id} onChange={e => setId(e.target.value)} placeholder="tpl_xxx" autoFocus/></div>}
          <div className="field"><label>Name</label><input className="input" value={name} onChange={e => setName(e.target.value)}/></div>
          <div className="field">
            <label>Trigger</label>
            <select className="select" value={trigger} onChange={e => setTrigger(e.target.value)}>
              <option value="">— Select —</option>
              <option value="deposit">Deposit</option>
              <option value="withdrawal">Withdrawal</option>
              <option value="maturity">Maturity reminder</option>
              <option value="approval">Finance approval</option>
              <option value="rejection">Finance rejection</option>
              <option value="defaulter">Defaulter notice</option>
            </select>
            <div className="hint">When the dispatcher should pick this template.</div>
          </div>
          <div className="field"><label>Channel</label><select className="select" value={channel} onChange={e => setChannel(e.target.value)}><option value="whatsapp">WhatsApp</option><option value="email">Email</option><option value="both">Both</option></select></div>
          <div className="field">
            <label>Scheme</label>
            <select className="select" value={schemeId} onChange={e => setSchemeId(e.target.value)}>
              <option value="">All schemes (global)</option>
              {(window.QH_DATA?.schemes || []).map(s => <option key={s.id} value={s.id}>{s.name} · {s.code}</option>)}
            </select>
            <div className="hint">A scheme-scoped template wins over the global one for that scheme.</div>
          </div>
          <div className="field" style={{gridColumn: '1 / -1'}}><label>Scope (descriptive label)</label><input className="input" value={scope} onChange={e => setScope(e.target.value)}/></div>
          <div className="field" style={{gridColumn: '1 / -1'}}>
            <label>Message body</label>
            <textarea className="textarea" rows={6} value={body} onChange={e => setBody(e.target.value)}/>
            <div className="hint">{`Variables: {{member}}, {{its}}, {{amount}}, {{scheme}}, {{scheme_code}}, {{schemeid}}, {{date}}, {{txn_id}}, {{maturity}}, {{member_balance}}, {{applicant}}, {{app_id}}, {{cheque}}, {{reason}}`}</div>
          </div>
          {err && <div style={{gridColumn: '1 / -1', color: 'var(--neg)', fontSize: 12}}>{err}</div>}
        </form>
        <div className="modal-footer">
          <button className="btn ghost" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn primary" onClick={submit} disabled={busy}><I.Check/> {busy ? 'Saving…' : (isEdit ? 'Save' : 'Create')}</button>
        </div>
      </div>
    </div>
  );
}

// ───────── User (invite + edit) ─────────
function UserModal({ onClose, user }) {
  const isEdit = !!user;
  const [name, setName] = useStateM(user?.name || '');
  const [username, setUsername] = useStateM(user?.username || '');
  const [email, setEmail] = useStateM(user?.email || '');
  const [role, setRole] = useStateM(user?.role || (QH_DATA.roles[0]?.id || 'data_entry'));
  const [status, setStatus] = useStateM(user?.status || 'active');
  const [password, setPassword] = useStateM('');
  const [busy, setBusy] = useStateM(false);
  const [err, setErr] = useStateM('');
  // Bank access editor state. Lazy-loaded on open for existing users so
  // the modal doesn't pay the request when the operator only wants to
  // tweak name/email. For new users we use the local bank list and
  // submit the chosen set after the user is created.
  const [bankRows, setBankRows] = useStateM(null);

  useEffectM(() => {
    if (isEdit) {
      api().get(`/users/${user.id}/banks`).then(r => setBankRows(r.banks)).catch(() => {});
    } else {
      // Pre-populate with every active bank checked — same default the
      // server applies on POST /users for non-admin users.
      const all = (window.QH_DATA?.banks || []).filter(b => b.active).map(b => ({
        id: b.id, name: b.name, code: b.code, active: b.active, allowed: true,
      }));
      setBankRows(all);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleBank = (id) => setBankRows(rows => rows.map(r => r.id === id ? { ...r, allowed: !r.allowed } : r));
  const setAllBanks = (v) => setBankRows(rows => rows.map(r => ({ ...r, allowed: v })));

  const submit = async (e) => {
    e?.preventDefault();
    if (!name || !username || !email || !role) { setErr('Name, username, email and role are all required.'); return; }
    if (!isEdit && (!password || password.length < 8)) { setErr('Password must be at least 8 characters.'); return; }
    setBusy(true); setErr('');
    try {
      // Normalise the username client-side so users see the same value
      // that gets stored. Server lowercases too, defensively.
      const u = (username || '').trim().toLowerCase();
      let userId = user?.id;
      if (isEdit) await api().put(`/users/${user.id}`, { name, username: u, email, role, status, password: password || null });
      else {
        const created = await api().post('/users', { name, username: u, email, role, password });
        userId = created?.id;
      }
      // Persist the bank-access set for non-admin users. Admin role bypasses
      // server-side and the response there is just a no-op.
      if (userId && role !== 'admin' && bankRows) {
        const bankIds = bankRows.filter(r => r.allowed).map(r => r.id);
        // Don't swallow this: a new user is seeded with ALL active banks
        // server-side, so if the restricting PUT fails silently the operator
        // is left with full visibility — the opposite of what was configured.
        try {
          await api().put(`/users/${userId}/banks`, { bankIds });
        } catch (be) {
          throw new Error(`User saved, but applying bank access failed — the user may still see all banks. Re-open the user and set their banks. (${be.message || 'request failed'})`);
        }
      }
      await reload();
      onClose();
    } catch (e) { setErr(e.message || 'Failed.'); } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!isEdit) return;
    if (!window.confirm(`Delete user ${user.name}?`)) return;
    setBusy(true);
    try {
      await api().delete(`/users/${user.id}`);
      await reload();
      onClose();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header"><h3>{isEdit ? 'Edit user' : 'Invite user'}</h3><button className="icon-btn close" onClick={onClose}><I.X/></button></div>
        <form className="modal-body" onSubmit={submit} style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12}}>
          <div className="field" style={{gridColumn: '1 / -1'}}><label>Full name</label><input className="input" value={name} onChange={e => setName(e.target.value)} autoFocus/></div>
          <div className="field"><label>Username</label>
            <input className="input font-mono" value={username}
                   onChange={e => setUsername(e.target.value)}
                   placeholder="e.g. mariam.salah" autoComplete="off"/>
            <div className="hint">3–32 chars · letters, digits, dot, underscore, hyphen.</div>
          </div>
          <div className="field"><label>Email</label>
            <input className="input" type="email" value={email}
                   onChange={e => setEmail(e.target.value)}
                   placeholder="user@qh.bh"/>
            <div className="hint">Used for OTP delivery.</div>
          </div>
          <div className="field"><label>Role</label><select className="select" value={role} onChange={e => setRole(e.target.value)}>{QH_DATA.roles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}</select></div>
          {isEdit && <div className="field"><label>Status</label><select className="select" value={status} onChange={e => setStatus(e.target.value)}><option value="active">Active</option><option value="inactive">Inactive</option></select></div>}
          <div className="field" style={{gridColumn: '1 / -1'}}>
            <label>{isEdit ? 'New password (leave blank to keep)' : 'Initial password'}</label>
            <input className="input font-mono" type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder={isEdit ? '—' : 'min 6 chars'}/>
          </div>
          {/* Per-user bank visibility. Admin role bypasses the ACL — show a
              read-only note so the operator understands why the picker is
              hidden. Non-admins get a checkbox grid with select-all/clear. */}
          <div className="field" style={{gridColumn: '1 / -1'}}>
            <label style={{display:'flex',alignItems:'center',gap:8}}>
              <span>Bank access</span>
              {role !== 'admin' && bankRows && (
                <span className="text-xs muted" style={{fontWeight:400,textTransform:'none',letterSpacing:0}}>
                  ({bankRows.filter(r => r.allowed).length} of {bankRows.length})
                </span>
              )}
              {role !== 'admin' && bankRows && (
                <span style={{marginLeft:'auto',display:'flex',gap:6}}>
                  <button type="button" className="btn ghost sm" onClick={() => setAllBanks(true)}>All</button>
                  <button type="button" className="btn ghost sm" onClick={() => setAllBanks(false)}>None</button>
                </span>
              )}
            </label>
            {role === 'admin' ? (
              <div className="text-xs muted" style={{padding:'8px 10px',background:'var(--bg-sunken)',borderRadius:4}}>
                Admin sees every bank — no per-user list is stored.
              </div>
            ) : bankRows ? (
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:6,maxHeight:200,overflow:'auto',padding:8,background:'var(--bg-sunken)',borderRadius:4}}>
                {bankRows.map(r => (
                  <label key={r.id} style={{display:'flex',alignItems:'center',gap:6,fontSize:13,cursor:'pointer'}}>
                    <input type="checkbox" checked={r.allowed} onChange={() => toggleBank(r.id)}/>
                    <span>{r.name}{r.code ? <span className="text-xs muted" style={{marginLeft:4}}>· {r.code}</span> : null}{!r.active && <span className="text-xs muted" style={{marginLeft:4}}>(inactive)</span>}</span>
                  </label>
                ))}
                {bankRows.length === 0 && <div className="text-xs muted">No banks defined yet.</div>}
              </div>
            ) : (
              <div className="text-xs muted">Loading…</div>
            )}
            {role !== 'admin' && bankRows && bankRows.filter(r => r.allowed).length === 0 && (
              <div className="text-xs" style={{color:'var(--warn)',marginTop:4}}>
                <I.AlertCircle s={11}/> Empty set — this user won't see any banks, transactions or balances.
              </div>
            )}
          </div>
          {err && <div style={{gridColumn: '1 / -1', color: 'var(--neg)', fontSize: 12}}>{err}</div>}
        </form>
        <div className="modal-footer">
          {isEdit && window.can('users','delete') && <button className="btn danger" onClick={remove} disabled={busy}><I.X/> Delete</button>}
          <div style={{flex: 1}}/>
          <button className="btn ghost" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn primary" onClick={submit} disabled={busy}><I.Check/> {busy ? 'Saving…' : (isEdit ? 'Save' : 'Invite')}</button>
        </div>
      </div>
    </div>
  );
}

// ───────── Topbar search dropdown ─────────
function SearchPalette({ go }) {
  const [q, setQ] = useStateM('');
  const [hits, setHits] = useStateM([]);
  const [open, setOpen] = useStateM(false);
  const [active, setActive] = useStateM(0);
  const tRef = useRefM(null);
  const wrapRef = useRefM(null);

  useEffectM(() => {
    if (!q || q.length < 2) { setHits([]); return; }
    if (tRef.current) clearTimeout(tRef.current);
    tRef.current = setTimeout(async () => {
      try {
        const r = await api().get('/search?q=' + encodeURIComponent(q));
        setHits(r);
        setActive(0);
      } catch {}
    }, 200);
    return () => tRef.current && clearTimeout(tRef.current);
  }, [q]);

  useEffectM(() => {
    const onClick = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  // Map a search hit to its (route, required-permission, label) tuple.
  // If the user lacks the required perm we surface a brief error instead
  // of navigating to a page they can't see.
  const targetFor = (h) => {
    if (h.kind === 'scheme')      return { route: 'scheme:' + h.id, requires: 'schemes',      label: 'Schemes' };
    if (h.kind === 'member')      return { route: 'member:' + h.id, requires: 'member_profile', label: 'Member Profile' };
    if (h.kind === 'event')       return { route: 'event:' + h.id,  requires: 'events',       label: 'Events' };
    if (h.kind === 'transaction') return { route: 'transactions',   requires: 'transactions', label: 'Transactions' };
    if (h.kind === 'finance_app') return { route: 'finance',        requires: 'finance_apps', label: 'Finance Apps' };
    if (h.kind === 'user')        return { route: 'rbac',           requires: 'users',        label: 'Users & Roles' };
    if (h.kind === 'bank')        return { route: 'banks',          requires: 'banks',        label: 'Banks' };
    return null;
  };

  const choose = (h) => {
    const t = targetFor(h);
    if (!t) { setOpen(false); setQ(''); return; }
    if (t.requires && !window.can?.(t.requires, 'read')) {
      // The role can't read the destination page — surface a short
      // notice instead of routing to a hidden screen. alert() keeps
      // this dependency-free; the topbar doesn't have a toast hook.
      alert(`You don't have access to ${t.label}.`);
      return;
    }
    go(t.route);
    setOpen(false); setQ('');
  };

  const keyDown = (e) => {
    if (!open || !hits.length) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive(i => Math.min(hits.length - 1, i + 1)); }
    if (e.key === 'ArrowUp') { e.preventDefault(); setActive(i => Math.max(0, i - 1)); }
    if (e.key === 'Enter') { e.preventDefault(); choose(hits[active]); }
    if (e.key === 'Escape') setOpen(false);
  };

  return (
    <div ref={wrapRef} className="topbar-search" style={{position: 'relative'}}>
      <I.Search/>
      <input
        placeholder="Search schemes, members, transactions, applications…"
        value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={keyDown}
      />
      {open && hits.length > 0 && (
        <div style={{position: 'absolute', top: '110%', left: 0, right: 0, background: 'var(--bg-elev)', border: '1px solid var(--line)', borderRadius: 4, boxShadow: '0 8px 24px rgba(0,0,0,0.08)', zIndex: 50, maxHeight: 360, overflowY: 'auto'}}>
          {hits.map((h, i) => (
            <button key={h.kind + h.id} type="button" onClick={() => choose(h)} className={`nav-item ${i === active ? 'active' : ''}`} style={{padding: '10px 14px', height: 'auto', alignItems: 'flex-start', flexDirection: 'column', gap: 2, width: '100%', textAlign: 'left', borderBottom: '1px solid var(--line)'}}>
              <div style={{display: 'flex', alignItems: 'center', gap: 8, width: '100%'}}>
                <span className="tag" style={{textTransform: 'uppercase', fontSize: 9}}>{h.kind}</span>
                <span style={{fontSize: 13, fontWeight: 500}}>{h.title}</span>
              </div>
              <div className="text-xs muted">{h.subtitle}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ───────── Self-service password change ─────────
function ChangePasswordModal({ onClose, onSuccess }) {
  const [current, setCurrent] = useStateM('');
  const [next, setNext] = useStateM('');
  const [confirm, setConfirm] = useStateM('');
  const [busy, setBusy] = useStateM(false);
  const [err, setErr] = useStateM('');

  const submit = async (e) => {
    e?.preventDefault();
    setErr('');
    if (!current || !next) { setErr('Current and new passwords are required.'); return; }
    if (next.length < 8) { setErr('New password must be at least 8 characters.'); return; }
    if (next !== confirm) { setErr('New password and confirmation do not match.'); return; }
    if (next === current) { setErr('New password must differ from the current one.'); return; }
    setBusy(true);
    try {
      await api().post('/auth/change-password', { currentPassword: current, newPassword: next });
      onSuccess?.();
    } catch (e) { setErr(e.message || 'Could not change password.'); }
    finally { setBusy(false); }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header"><h3>Change password</h3><button className="icon-btn close" onClick={onClose}><I.X/></button></div>
        <form className="modal-body" onSubmit={submit} style={{display: 'flex', flexDirection: 'column', gap: 12}}>
          <div className="field"><label>Current password</label>
            <input className="input" type="password" value={current} onChange={e => setCurrent(e.target.value)} autoFocus autoComplete="current-password"/>
          </div>
          <div className="field"><label>New password</label>
            <input className="input" type="password" value={next} onChange={e => setNext(e.target.value)} autoComplete="new-password"/>
            <div className="hint">Minimum 6 characters.</div>
          </div>
          <div className="field"><label>Confirm new password</label>
            <input className="input" type="password" value={confirm} onChange={e => setConfirm(e.target.value)} autoComplete="new-password"/>
          </div>
          {err && <div style={{color: 'var(--neg)', fontSize: 12}}>{err}</div>}
          <div className="text-xs muted">Other devices will be signed out. Your current session stays active.</div>
        </form>
        <div className="modal-footer">
          <button className="btn ghost" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn primary" onClick={submit} disabled={busy}><I.Check/> {busy ? 'Saving…' : 'Update password'}</button>
        </div>
      </div>
    </div>
  );
}

window.SchemeCreateModal = SchemeCreateModal;
window.EventCreateModal = EventCreateModal;
window.TemplateModal = TemplateModal;
window.UserModal = UserModal;
window.SearchPalette = SearchPalette;
window.ChangePasswordModal = ChangePasswordModal;
